import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { addCreatedCompany, dashboardPath, filesPath, startCompany, startPath, useCompanies } from '../features/companies/hooks';
import { UnverifiedEmailNote, useInvitationActions, useMyInvitations } from '../features/companies/invitations';
import { useI18n } from '../i18n';
import { EmptyState, Icon, Spinner, useToast } from '../ui';
import { CompanyDetailsForm } from '../features/companies/CompanyDetailsForm';
import type { Company } from '../api/types';

/**
 * The start page: straight to the company last used, or — for somebody in no company yet —
 * create one or accept an invitation. Also where links in notification emails land
 * (/?members, /?files, /?invitations, /?company, /?tasks&tenant=…&task=… …).
 */
export function Home() {
  const { t } = useI18n();
  const companies = useCompanies();
  const [params] = useSearchParams();
  if (companies.isLoading) return <Spinner label={t('common.loading')} />;
  const list = companies.data ?? [];
  // A link that names its company (a task's e-mail, the integration hub coming back) goes there;
  // anything else to the company last used - one where I am a member rather than a guest, when
  // there is one (TF-78).
  const target = startCompany(list, params.get('tenant'));
  // Back from a provider's sign-in page.
  if (params.has('integrations') && target) {
    const back = new URLSearchParams({ result: params.get('integrations') ?? '', provider: params.get('provider') ?? '' });
    // A Microsoft 365 or Gmail mailbox is each person's own, connected on their account page (TF-323, TF-324).
    if (params.get('provider') === 'microsoft-mail' || params.get('provider') === 'google-mail') return <Navigate to={`/account?${back.toString()}&tenant=${target.id}#mail-logging`} replace />;
    // So is a calendar (TF-325).
    if (params.get('provider') === 'google-calendar' || params.get('provider') === 'microsoft-calendar') {
      return <Navigate to={`/account?${back.toString()}&tenant=${target.id}#calendar-sync`} replace />;
    }
    // Google Contacts and Microsoft 365 contacts are connected from their imports (TF-237, TF-311), by any member: back there.
    const imports: Record<string, string> = { 'google-contacts': 'crm/import/google', 'microsoft-contacts': 'crm/import/microsoft' };
    const page = imports[params.get('provider') ?? ''] ?? 'settings/integrations';
    return <Navigate to={`/c/${target.id}/${page}?${back.toString()}`} replace />;
  }
  if (target && !params.has('invitations')) {
    if (params.has('members')) return <Navigate to={`/c/${target.id}/settings/members`} replace />;
    // "Add the company's number" (TF-113): its details are on Settings, Company.
    if (params.has('company') && target.role === 'owner') return <Navigate to={`/c/${target.id}/settings/company`} replace />;
    if (params.has('privacy') || params.has('automation')) {
      const section = params.has('privacy') ? 'privacy' : 'automation';
      return <Navigate to={`/c/${target.id}/settings/${section}`} replace />;
    }
    if (params.has('tasks') && target.role !== 'external') {
      const task = params.get('task');
      return <Navigate to={`/c/${target.id}/crm/tasks${task ? `?task=${encodeURIComponent(task)}` : ''}`} replace />;
    }
    // A team notice's e-mail (TF-301): the deal, the document or the team it is about.
    if (params.has('deals') && target.role !== 'external') {
      const deal = params.get('deal');
      return <Navigate to={`/c/${target.id}/crm/deals${deal ? `/${encodeURIComponent(deal)}` : ''}`} replace />;
    }
    if (params.has('teams')) {
      const team = params.get('team');
      return <Navigate to={`/c/${target.id}/settings/teams${team ? `/${encodeURIComponent(team)}` : ''}`} replace />;
    }
    if (params.has('files') && params.get('file') && target.role !== 'external') return <Navigate to={`/c/${target.id}/file/${encodeURIComponent(params.get('file')!)}`} replace />;
    // "Your upload was blocked", "the signed document is next to the original": those e-mails
    // mean the drive, and they said so before the dashboard existed.
    if (params.has('files') && target.role !== 'external') return <Navigate to={filesPath(target.id)} replace />;
    // A guest's company has no dashboard for them: what is shared with them is where they start.
    return <Navigate to={startPath(target)} replace />;
  }
  return <Onboarding hasCompanies={list.length > 0} held={list.filter((company) => company.status === 'held')} />;
}

function Onboarding({ hasCompanies, held }: { hasCompanies: boolean; held: Company[] }) {
  const { t } = useI18n();
  return (
    <div className="stack max-w-narrow">
      {/* A company held while its registration is checked (TF-213): what happens next. */}
      {held.map((company) => (
        <section key={company.id} className="card" aria-label={company.name}>
          <h2>
            <Icon name="schedule" className="icon-lead" />
            {company.name}
          </h2>
          <p>{t('company.heldNote')}</p>
        </section>
      ))}
      <InvitationsForMe />
      {!hasCompanies && (
        <div className="card">
          <EmptyState icon={<Icon name="apartment" size={40} />} title={t('company.none')}>
            <p>{t('company.noneHint')}</p>
          </EmptyState>
          <CreateCompanyForm />
        </div>
      )}
    </div>
  );
}

/** Registering a company (TF-79): its name and who it is, the same form as the switcher's. */
function CreateCompanyForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  return (
    <CompanyDetailsForm
      mode="register"
      onSaved={async (company) => {
        await addCreatedCompany(queryClient, company);
        // Held while its registration is checked (TF-213): the start page says so.
        if (company.status === 'held') return;
        toast.info(t('company.created', { name: company.name }));
        navigate(dashboardPath(company.id));
      }}
    />
  );
}

/** Invitations addressed to my (verified) email: accepting is just clicking; declining too (TF-78). */
export function InvitationsForMe() {
  const { t, date } = useI18n();
  const invitations = useMyInvitations();
  const { accept, decline } = useInvitationActions();
  if (invitations.data?.unverifiedEmail) return <UnverifiedEmailNote email={invitations.data.unverifiedEmail} />;
  if (!invitations.data?.invitations.length) return null;
  return (
    <section className="card" aria-labelledby="my-invitations">
      <h2 id="my-invitations">
        <Icon name="mail" className="icon-lead" />
        {t('invitations.title')}
      </h2>
      <ul className="list">
        {invitations.data.invitations.map((invitation) => (
          <li key={invitation.id}>
            <div className="grow">
              <strong>{invitation.tenantName}</strong>
              <div className="small muted">
                {t('invitations.from', { name: invitation.invitedByName ?? '—', role: t(`role.${invitation.role}`) })} · {t('members.expires', { date: date(invitation.expiresAt) })}
              </div>
            </div>
            <div className="btn-row">
              <button type="button" className="btn btn-tonal" disabled={accept.isPending} onClick={() => accept.mutate(invitation.id)}>
                {t('invitations.accept')}
              </button>
              <button type="button" className="btn" disabled={decline.isPending} onClick={() => decline.mutate(invitation)}>
                {t('invitations.decline')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
